import i18n from '@/i18n'
import dayjs from 'dayjs'
import { RequestStatus, RequestType, UserRole } from '@/types/enums'
import type { Request } from '@/types/request.types'

export function isLeaveRequestType(type: string): boolean {
  return (
    type === RequestType.LEAVE_PAID ||
    type === RequestType.LEAVE_UNPAID ||
    type === RequestType.LEAVE_REPORT
  )
}

/**
 * Çalışan portalindeki bekleyen talep rozeti.
 *
 * Çalışana onayın hangi kademede olduğu gösterilmez; şirket içi onay
 * hiyerarşisi bilgisi kendisini ilgilendirmiyor. Yalnız sürecin devam
 * ettiği bilgisi verilir. Aşama detayı yönetim panelinde gösterilir.
 */
export function getPendingWorkflowHint(
  req: Pick<Request, 'status'>,
): string {
  if (req.status !== RequestStatus.PENDING) return ''
  return i18n.global.t('portal.requests.underReview')
}

/**
 * Avans onay aşamaları (yeni akış):
 *  1 - Operasyon Yetkilisi
 *  2 - Operasyon Müdürü
 *  3 - Müdür (varsa, org şemasında müdürün üstündeki kademe)
 *
 * Onaylandıktan sonra HR_MANAGER ve FINANCE aşaması yoktur; bunlar post-flag (ödendi / bordroya işlendi)
 * üzerinden yürür.
 */
export function getAdvanceApprovalStageLabel(level: number): string {
  const labels: Record<number, string> = {
    1: i18n.global.t('workflow.opsOfficer'),
    2: i18n.global.t('workflow.opsManager'),
    3: i18n.global.t('workflow.manager'),
    // Onaylandıktan sonra bordro işlemeden önce İK Müdürü'nün reddetme aşaması
    4: i18n.global.t('workflow.hrManager'),
  }
  return labels[level] || i18n.global.t('workflow.advanceApproval')
}

export function getLeaveApprovalStageLabel(level: number): string {
  const labels: Record<number, string> = {
    0: i18n.global.t('workflow.projectSupervisor'),
    1: i18n.global.t('workflow.opsOfficer'),
    2: i18n.global.t('workflow.opsManager'),
    3: i18n.global.t('workflow.manager'),
    4: i18n.global.t('workflow.hrManager'),
  }
  return labels[level] || i18n.global.t('workflow.leaveApproval')
}

/**
 * İzin akışında eski "3. aşama" (Müdür) kaldırıldığı için mevcut süreç
 * doğrudan 2'den 4'e atlıyor. Bu iç seviye numarası kullanıcıya "4." olarak
 * sızıp sanki bir aşama atlanmış gibi kafa karıştırmasın diye görüntülenen
 * sıraya çevrilir (4 → 3). Avansta tüm kademeler hâlâ gerçek/ardışık olduğu
 * için dokunulmaz.
 */
export function displayApprovalStage(type: string, level: number): number {
  if (type !== RequestType.ADVANCE && level >= 4) return 3
  return level
}

/** Tablo sütunu: kısa aşama etiketi */
export function getApprovalStageColumnLabel(
  row: Pick<
    Request,
    | 'status'
    | 'type'
    | 'approvalLevel'
    | 'secondApprovedAt'
    | 'managerApprovedAt'
    | 'advancePayrollRecordedAt'
    | 'advancePaidAt'
  >,
): string {
  if (row.status === RequestStatus.PENDING) {
    const level = row.approvalLevel ?? 1
    const shown = displayApprovalStage(row.type, level)
    if (row.type === RequestType.ADVANCE) {
      return `${shown} · ${getAdvanceApprovalStageLabel(level)}`
    }
    return `${shown} · ${getLeaveApprovalStageLabel(level)}`
  }

  if (row.status === RequestStatus.APPROVED) {
    if (row.type === RequestType.ADVANCE) {
      // Onay sonrası sıra: İK bordroya işler → Finans öder
      if (!row.advancePayrollRecordedAt) {
        return i18n.global.t('workflow.approvedAwaitingPayroll')
      }
      if (!row.advancePaidAt) {
        return i18n.global.t('workflow.approvedAwaitingFinance')
      }
      return i18n.global.t('workflow.advanceCompleted')
    }
    return i18n.global.t('workflow.processedHr')
  }

  if (row.status === RequestStatus.REJECTED) {
    const level = row.approvalLevel ?? 1
    const shown = displayApprovalStage(row.type, level)
    if (row.type === RequestType.ADVANCE) {
      return `${shown} · Red (${getAdvanceApprovalStageLabel(level)})`
    }
    return `${shown} · Red (${getLeaveApprovalStageLabel(level)})`
  }

  return '—'
}

/**
 * Mevcut kullanıcının bu talep satırında onay/red verip veremeyeceği (backend kurallarıyla uyumlu).
 * Avans için onay aşamaları yalnızca 1-3'tür; HR_MANAGER ve FINANCE artık post-flag rolleridir.
 */
export function canUserDecideOnRequest(
  row: Pick<
    Request,
    | 'status'
    | 'type'
    | 'approvalLevel'
    | 'approverId'
    | 'secondApproverId'
    | 'thirdApproverId'
    | 'currentApproverIds'
  >,
  user: { id: string; role: UserRole } | null,
): boolean {
  if (!user || row.status !== RequestStatus.PENDING) return false
  const level = row.approvalLevel ?? 1
  const isAdmin = user.role === UserRole.ADMIN || user.role === UserRole.SUPER_ADMIN

  // Backend o aşamada karar verebilecek herkesi bildirir (proje amirleri ve
  // çoklu sorumlular tek bir approverId'ye sığmadığı için).
  if (row.currentApproverIds?.length) {
    return isAdmin || row.currentApproverIds.includes(user.id)
  }

  if (row.type === RequestType.ADVANCE) {
    if (isAdmin) return true
    if (level === 1) return !!row.approverId && user.id === row.approverId
    if (level === 2) return !!row.secondApproverId && user.id === row.secondApproverId
    if (level === 3) return !!row.thirdApproverId && user.id === row.thirdApproverId
    return false
  }

  if (isAdmin) return true
  if (level === 1) return !!row.approverId && user.id === row.approverId
  if (level === 2) return !!row.secondApproverId && user.id === row.secondApproverId
  if (level === 3) return !!row.thirdApproverId && user.id === row.thirdApproverId
  if (level === 4) return user.role === UserRole.HR_MANAGER
  return false
}

/** Takvim günü: başlangıç >= bugün (başlangıç günü dahil) — backend ile aynı mantık */
export function isRequestStartDateNotYetPassed(startDate: string | Date | undefined): boolean {
  if (startDate == null || startDate === '') return false
  const s = dayjs(startDate).format('YYYY-MM-DD')
  const today = dayjs().format('YYYY-MM-DD')
  return s >= today
}

/**
 * 2. aşamada onaylanmış talep: üst yönetici (veya admin), başlangıç tarihi gelmeden düzenleyebilir.
 */
export function canSecondApproverAmendApproved(
  row: Pick<
    Request,
    'status' | 'type' | 'approvalLevel' | 'secondApproverId' | 'secondApprovedAt' | 'startDate'
  >,
  user: { id: string; role: UserRole } | null,
): boolean {
  if (!user) return false
  if (row.type === RequestType.ADVANCE) return false
  if (row.status !== RequestStatus.APPROVED) return false
  if (!row.secondApprovedAt) return false
  if ((row.approvalLevel ?? 1) !== 2) return false
  if (!isRequestStartDateNotYetPassed(row.startDate)) return false

  if (user.role === UserRole.ADMIN || user.role === UserRole.SUPER_ADMIN) return true
  if (!row.secondApproverId) return false
  return user.id === row.secondApproverId
}

/**
 * Reddedilmiş talep: red veren aşamadaki yönetici (veya admin), başlangıç tarihi gelmeden düzenleyebilir.
 * 1. aşama red → atanan yönetici; 2. aşama red → üst yönetici (reject sonrası approverId üst yöneticiye yazılır).
 */
export function canManagerAmendRejected(
  row: Pick<Request, 'status' | 'type' | 'approvalLevel' | 'approverId' | 'secondApproverId' | 'startDate'>,
  user: { id: string; role: UserRole } | null,
): boolean {
  if (!user || row.status !== RequestStatus.REJECTED) return false
  if (!isRequestStartDateNotYetPassed(row.startDate)) return false

  if (user.role === UserRole.ADMIN || user.role === UserRole.SUPER_ADMIN) return true

  if (row.type === RequestType.ADVANCE) {
    // 4. aşama = İK Müdürü'nün bordro öncesi reddi; backend bunu düzeltme akışına açmaz
    return (row.approvalLevel ?? 1) < 4 && row.approverId === user.id
  }

  const level = row.approvalLevel ?? 1
  if (level === 1) {
    if (!row.approverId) return false
    return user.id === row.approverId
  }
  if (!row.secondApproverId) return false
  return user.id === row.secondApproverId
}

/** Onaylı izin düzeltmesi legacy helper; avans final onay sonrası bu ekrandan düzeltilmez. */
export function shouldShowPayableAmountField(
  row: Pick<Request, 'type' | 'approvalLevel'>,
): boolean {
  return row.type === RequestType.ADVANCE && (row.approvalLevel ?? 1) === 2
}

/** Bekleyen avans: her onay aşaması ödenecek tutarı belirleyebilir */
export function shouldShowPayableAmountFieldPendingDecision(
  row: Pick<Request, 'type' | 'status'>,
): boolean {
  return row.type === RequestType.ADVANCE && row.status === RequestStatus.PENDING
}

export type RequestApproverSnippet = {
  firstName?: string | null
  lastName?: string | null
  role?: string | null
}

/**
 * Red, hangi aşamada verildiyse approvalLevel o aşamada kalır.
 */
export function getRejectionSourceLabel(
  row: Pick<Request, 'status' | 'type' | 'approvalLevel'>,
  approver?: RequestApproverSnippet | null,
): string {
  if (row.status !== RequestStatus.REJECTED) return ''
  const level = row.approvalLevel ?? 1
  let stage: string
  if (row.type === RequestType.ADVANCE) {
    stage = getAdvanceApprovalStageLabel(level)
  } else {
    stage = getLeaveApprovalStageLabel(level)
  }
  const name = [approver?.firstName, approver?.lastName].filter(Boolean).join(' ').trim()
  if (name) return `${stage} — ${name}`
  return stage
}
